import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '../common/Button';
import { ProfileDropdown } from '../common/ProfileDropdown';
import { logout } from '../../redux/slices/authSlice';
import type { RootState } from '../../redux/store';
import snapmenuLogo from '../../assets/snapmenu.png';

interface HeaderProps {}

export const Header: React.FC<HeaderProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginUser, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  // Check if current page is analytics page
  const isAnalyticsPage = location.pathname === '/analytics' || 
                          location.pathname.startsWith('/analytics/');

  const handleLogout = () => {
    dispatch(logout());
  };

  return (
    <header className="sticky top-0 z-50 bg-[var(--bg-primary)] border-b border-[var(--border-color)] backdrop-blur-sm">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Logo and Brand */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <img 
                src={snapmenuLogo} 
                alt="SnapMenuAI Logo" 
                className="h-12 object-contain"
              />
            </div>
          </div>

          {/* Right side - Company Selector (Superadmin) + Analytics Button + User Menu or Auth Buttons */}
          <div className="flex items-center space-x-4">
            {/* Company Selector Button - Only for Superadmin */}
            {isAuthenticated && loginUser?.role?.toLowerCase() === 'superadmin' && (
              <button
                onClick={() => navigate('/companies')}
                className="flex items-center space-x-2 px-4 py-2 border border-[var(--accent-color)] text-[var(--accent-color)] rounded-md hover:bg-[var(--accent-color)]/10 transition-all duration-200"
                title="Click to select company"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="font-semibold hidden sm:inline">
                  Select Company
                </span>
                <span className="font-semibold sm:hidden">Company</span>
              </button>
            )}

            {/* Analytics Button - Hidden on Analytics page */}
            {!isAnalyticsPage && (
              <button
                onClick={() => navigate('/analytics')}
                className="flex items-center space-x-2 px-4 py-2 border border-[var(--accent-color)] text-[var(--accent-color)] rounded-md hover:bg-[var(--accent-color)]/10 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="font-semibold hidden sm:inline">Analytics</span>
              </button>
            )}

            {isAuthenticated ? (
              <ProfileDropdown
                user={loginUser!}
                onChangePassword={() => navigate('/update-password')}
                onLogout={handleLogout}
              />
            ) : (
              <div className="flex items-center space-x-2">
                <Button 
                  variant="secondary" 
                  size="small"
                  className="bg-transparent text-[var(--text-primary)] border-[var(--border-color)] hover:bg-[var(--bg-hover)]"
                >
                  Login
                </Button>
                <Button 
                  size="small"
                  className="bg-[var(--accent-color)] text-white hover:opacity-90 border-[var(--accent-color)]"
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
